import { type DdosAccess } from '@shared/all/domains/ddosMode';

// Where DDoS mode sends a web visitor (.specs/ddos-mode.md). Pure, so the
// no-loop rules below are tested without a browser.

export const LOGIN_PATH = '/login';
export const HUMAN_VERIFICATION_PATH = '/biometric-verification';
// The app's one return-to convention: `/login?redirect=<same-origin path>`.
export const REDIRECT_PARAM = 'redirect';
const HOME_PATH = '/';

// Pages that work with no API access, or whose only calls are on the server's
// logged-out allowlist: sign-in, sign-up and recovery, the verification page,
// the pre-signup funnel, and the legal and marketing pages. Never redirected
// away from, which is what makes a redirect loop impossible.
const OPEN_PATHS = [
  LOGIN_PATH,
  '/signup',
  '/reset-password',
  '/magic-link',
  '/verify-email',
  HUMAN_VERIFICATION_PATH,
  '/onboarding',
  '/about',
  '/contact',
  '/privacy',
  '/terms',
  '/cookies',
  '/aup',
  '/eula',
  '/dsar',
  '/credits'
];

const isOpenPath = (pathname: string): boolean =>
  OPEN_PATHS.some(
    (open) => pathname === open || pathname.startsWith(`${open}/`)
  );

/** Only a same-origin path: guards against `?redirect=https://evil.com`. */
export const safeRedirect = (raw: string | null): string => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return HOME_PATH;
  return raw;
};

const withReturnTo = (target: string, from: string): string =>
  from === HOME_PATH
    ? target
    : `${target}?${REDIRECT_PARAM}=${encodeURIComponent(from)}`;

type Visit = {
  access: DdosAccess;
  /** null while the session is still being read: decide nothing yet. */
  isAuthenticated: boolean | null;
  pathname: string;
  /** `window.location.search`, kept so the visitor returns to the same view. */
  search: string;
};

/**
 * The URL to replace the current one with, or null to stay.
 *
 * Each refusal is only acted on when the client's own view of the session
 * agrees with it. A visitor who has just signed in is `isAuthenticated` before
 * the status is re-read, and sending them back to /login on the stale
 * `loginRequired` would bounce them between the two pages.
 */
export const ddosRedirectFor = ({
  access,
  isAuthenticated,
  pathname,
  search
}: Visit): string | null => {
  if (access === 'open' || isAuthenticated === null) return null;
  if (isOpenPath(pathname)) return null;

  if (access === 'loginRequired') {
    // Logged out, `/` is the static marketing page.
    if (isAuthenticated || pathname === HOME_PATH) return null;
    return withReturnTo(LOGIN_PATH, `${pathname}${search}`);
  }

  if (!isAuthenticated) return null;
  return withReturnTo(HUMAN_VERIFICATION_PATH, `${pathname}${search}`);
};
